'use client';

import React, { Suspense } from 'react';

import { Stack, Heading, Flex, Box } from '@chakra-ui/react';
import Link from 'next/link';

import { CustomText } from '@components/Custom-text';
import { UserType } from '@enums/index';
import { paths } from '@routes/path';

import { useGetDomainsQuery } from '../../../common/api';
import AuthFooter from '../../common/components/AuthFooter';
import useSignup from '../../hooks/useSignup';
import SignUpForm from '../forms/SignUpForm';

interface RegisterProps {
    userType: UserType;
}

const Register = ({ userType }: RegisterProps) => {
    const { data } = useGetDomainsQuery();
    const { handleSignup, isLoading } = useSignup({ userType });

    return (
        <Suspense fallback={<p>Loading feed...</p>}>
            <Flex
                minWidth="100%"
                justifyContent="center"
                flexDirection="column"
                position="relative"
                minHeight="100vh"
            >
                <Box maxH="95vh" overflow="scroll" className="hide-scroll">
                    <Stack spacing={8} width="100%" justifyContent="center" pt={2} px={1}>
                        <Stack width="100%" alignItems="center">
                            <Heading size="xl" color="titleText" textAlign="center">
                                Signing up as a {userType === UserType.Mentor ? 'Mentor' : 'Mentee'}
                            </Heading>
                            <CustomText color="subText" paddingLeft={4}>
                                Enter your sign up details below
                            </CustomText>
                        </Stack>
                        <Box>
                            <SignUpForm
                                onSubmit={values => handleSignup(values)}
                                isLoading={isLoading}
                                userType={userType}
                                domainData={data?.domainArray!}
                            />
                            <Stack my={10} width="100%" alignItems="center">
                                <Flex columnGap={1}>
                                    <CustomText color="titleText">
                                        Already have a{' '}
                                        {userType === UserType.Mentor ? 'Mentor' : 'Mentee'}{' '}
                                        account?
                                    </CustomText>
                                    <Link
                                        href={
                                            userType === UserType.Mentor
                                                ? paths.mentor.auth.login
                                                : paths.mentee.auth.login
                                        }
                                    >
                                        <CustomText color="primary.light" weight={500}>
                                            Sign In
                                        </CustomText>
                                    </Link>
                                </Flex>
                                <Flex columnGap={1}>
                                    <CustomText color="titleText">
                                        Not a {userType === UserType.Mentor ? 'Mentor' : 'Mentee'}?
                                    </CustomText>
                                    <Link
                                        href={
                                            userType === UserType.Mentor
                                                ? paths.mentee.auth.signup
                                                : paths.mentor.auth.signup
                                        }
                                    >
                                        <CustomText color="primary.light" weight={500}>
                                            Sign Up
                                        </CustomText>
                                    </Link>
                                    <CustomText color="titleText">
                                        as a {userType === UserType.Mentor ? 'Mentee' : 'Mentor'}
                                    </CustomText>
                                </Flex>
                            </Stack>
                        </Box>
                    </Stack>
                    <AuthFooter isRelative />
                </Box>
            </Flex>
        </Suspense>
    );
};

export default Register;
